/**
 * Tax Question V2 API service (S3-P2 — versioned question-set / conversation builder).
 *
 * Promise-based wrapper around the Wave-1 backend endpoints under
 * `api/v1/TaxQuestionV2` (TaxQuestionV2Controller). Mirrors the axios/base-URL/
 * cookie-auth + ResponseModel `unwrap`/`toError` pattern of
 * `services/taxConfigV2Api.js`. All responses are the standard ResponseModel
 * `{ Message, Status, Data }`; the helpers below unwrap `.Data` and throw on a
 * non-success `Status` (APIStatus.Successful === 0) so callers can `await` the
 * inner payload directly and `catch` a friendly Error.
 *
 * V2 is authoring-only (SCOPE FENCE — NO CALC): `FieldKey` is a plain identifier
 * (R1 — no more RuleCode), nothing computes with it. Everything here is camelCase
 * end-to-end (the V2 DTOs are camelCase).
 *
 * NOTE (JS project — no TypeScript): shapes documented via JSDoc typedefs.
 *
 * @typedef {Object} TaxQuestionVersionV2Dto
 * @property {number} questionVersionID @property {number} taxConfigVersionID @property {string} versionCode
 * @property {?string} description @property {?string} resumePromptTh @property {boolean} isActive
 *
 * @typedef {Object} TaxQuestionStepV2Dto
 * @property {number} questionStepID @property {string} stepCode @property {?string} label
 * @property {?string} answerSystemPrompt @property {?string} questionSystemPrompt
 * @property {?string[]} questionKnowledgeIds @property {?string[]} answerKnowledgeIds
 * @property {?string[]} questionKnowledgeCategories @property {?string[]} answerKnowledgeCategories
 * @property {?string} questionKnowledgeQuery
 * @property {number} sortOrder @property {boolean} isActive
 *
 * @typedef {Object} TaxQuestionItemV2Dto
 * @property {number} questionItemID @property {string} itemKind @property {?string} fieldKey
 * @property {?string} questionTextTh @property {?string} label @property {?string} helpText
 * @property {?string} questionSystemPrompt @property {?string} answerSystemPrompt
 * @property {?string} inputType @property {?string} answerType
 * @property {?(string|Object)[]} answerOptions Quick replies; multilingual objects use `{ label, labelEn, labelZh }` and may preserve `value`.
 * @property {boolean} quickReplyPreferConfig
 * @property {?Object[]} incomeCategories
 * @property {?string[]} questionKnowledgeIds @property {?string[]} answerKnowledgeIds
 * @property {?string} questionKnowledgeQuery
 * @property {string} visibleWhen @property {boolean} isRequired
 * @property {?string} groupCode @property {number} sortOrder @property {boolean} isActive
 *
 * @typedef {Object} TaxQuestionGroupV2Dto
 * @property {string} groupCode @property {string} groupLabel
 * @property {?string} questionSystemPrompt
 * @property {?string[]} questionKnowledgeIds @property {?string[]} answerKnowledgeIds
 * @property {?string[]} questionKnowledgeCategories @property {?string[]} answerKnowledgeCategories
 * @property {?string} questionKnowledgeQuery
 * @property {number} sortOrder @property {TaxQuestionItemV2Dto[]} items
 *
 * @typedef {Object} TaxQuestionSnapshotV2Dto
 * @property {number} snapshotID @property {number} questionVersionID @property {number} snapshotNo
 * @property {string} sourceAction 'SAVE' | 'BASELINE' | 'RESTORE' | 'API_EDIT' (แก้รายข้อผ่าน REST/MCP)
 * @property {?number} restoredFromNo @property {?string} comment
 * @property {number} stepCount @property {number} itemCount
 * @property {?string} createdBy @property {string} createdDate
 * @property {boolean} isCurrent
 * @property {?string} payloadJson Raw JSON `{steps,items}` — only on `getSnapshot`, null in the list.
 *
 * @typedef {Object} TaxQuestionFullV2Dto
 * @property {TaxQuestionVersionV2Dto} version @property {TaxQuestionStepV2Dto[]} steps
 * @property {TaxQuestionGroupV2Dto[]} groups @property {string[]} warnings
 *
 * @typedef {Object} TaxQuestionBundleSourceV2Dto Provenance of an export — DISPLAY ONLY.
 * @property {?string} taxYearCode @property {?number} beYear @property {?string} configVersionCode
 * @property {?string} questionVersionCode @property {?string} description @property {?string} resumePromptTh
 *
 * @typedef {Object} TaxQuestionBundleV2Dto The export/import file. Cross-machine portable: every
 *   reference is a string code (`fieldKey` / `groupCode` / `functionCode` / `calcVersionCode`) — no
 *   server ids and no audit columns survive the export.
 * @property {string} kind Always `'lhb.taxquestion.v2.bundle'` — sniff this before touching anything else.
 * @property {string} schemaVersion @property {string} exportedAt @property {?string} exportedBy
 * @property {TaxQuestionBundleSourceV2Dto} source
 * @property {Object[]} steps @property {Object[]} items
 *
 * @typedef {Object} ImportTaxQuestionBundleV2Result
 * @property {?number} questionVersionID The new version's id — `null` on a dry run (nothing was created).
 * @property {string} versionCode The code actually used, after normalization + collision handling.
 * @property {boolean} versionCodeAdjusted `true` when a collision forced `Q3` → `Q3-2`.
 * @property {number} stepCount @property {number} itemCount
 * @property {number} droppedCalcFunctionCount @property {number} droppedCalcConfigCount
 * @property {boolean} dryRun
 * @property {string[]} warnings Thai, human-readable. Missing `fieldKey`s / dropped formulas do NOT
 *   fail the import — they land here, so show them.
 */

import axios from 'axios';

const BASE_URL = import.meta.env.VITE_APP_API || 'http://localhost:5000/';

// Dedicated instance. `withCredentials` is set explicitly (cookie session auth)
// so it does not depend on the global axios default being applied first.
const http = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

const API_STATUS_SUCCESS = 0; // APIStatus.Successful

/**
 * Unwrap a ResponseModel `{ message, status, data }` → inner `data`.
 * Throws an Error (with `.status`/`.data`) when the envelope reports failure.
 * Accepts either PascalCase (`Status`/`Data`) or camelCase (`status`/`data`).
 */
function unwrap(response) {
  const body = response && response.data;
  if (body && typeof body === 'object' && ('status' in body || 'Status' in body)) {
    const status = 'status' in body ? body.status : body.Status;
    const data = 'data' in body ? body.data : body.Data;
    if (status === API_STATUS_SUCCESS) {
      return data;
    }
    const err = new Error(body.message || body.Message || 'Request failed');
    err.status = status;
    err.data = data;
    throw err;
  }
  return body;
}

/** Normalize an axios/transport error into a friendly Error carrying the API message. */
function toError(error) {
  const body = error && error.response && error.response.data;
  const message =
    (body && (body.message || body.Message)) ||
    (error && error.message) ||
    'Request failed';
  const err = new Error(message);
  err.httpStatus = error && error.response && error.response.status;
  err.status = body && (body.status ?? body.Status);
  err.data = body && (body.data ?? body.Data);
  return err;
}

async function get(url) {
  try {
    return unwrap(await http.get(url));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}
async function post(url, payload) {
  try {
    return unwrap(await http.post(url, payload));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}
async function put(url, payload) {
  try {
    return unwrap(await http.put(url, payload));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}
async function patch(url, payload) {
  try {
    return unwrap(await http.patch(url, payload));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}
async function del(url) {
  try {
    return unwrap(await http.delete(url));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}

const ROOT = 'api/v1/TaxQuestionV2';

// App Bar options live on the Bar Management controller, NOT under ROOT. The list is the one master copy
// (`conditionStateOptions`), so the page never types the four values itself.
const BAR_OPTIONS_URL = 'api/v1/BarManagement/options';

// Module-level cache: the endpoint also hits the database for segment/wealth lists, so load it once per page.
// A failed load clears the cache so the next dialog open tries again instead of replaying the rejection.
let stateGroupOptionsPromise = null;

export const taxQuestionV2Api = {
  // ── Question Versions ────────────────────────────────────────────────
  /**
   * List question versions, optionally filtered by the bound config version.
   * @param {?number} configVersionId
   * @param {boolean} includeInactive
   * @returns {Promise<TaxQuestionVersionV2Dto[]>}
   */
  getVersions(configVersionId = null, includeInactive = true) {
    let url = `${ROOT}/versions?includeInactive=${includeInactive}`;
    if (configVersionId != null) url += `&configVersionId=${configVersionId}`;
    return get(url);
  },
  /** @returns {Promise<TaxQuestionVersionV2Dto>} */
  getVersion(id) {
    return get(`${ROOT}/versions/${id}`);
  },
  /** @returns {Promise<TaxQuestionFullV2Dto>} version + steps + grouped items — load builder */
  getFull(id, includeInactive = true) {
    return get(`${ROOT}/versions/${id}/full?includeInactive=${includeInactive}`);
  },
  /** @returns {Promise<TaxQuestionVersionV2Dto>} */
  createVersion(body) {
    return post(`${ROOT}/versions`, body);
  },
  /** @returns {Promise<TaxQuestionVersionV2Dto>} */
  updateVersion(id, body) {
    return put(`${ROOT}/versions/${id}`, body);
  },
  /** @returns {Promise<boolean>} (siblings auto-deactivated server-side) */
  setVersionActive(id, isActive) {
    return patch(`${ROOT}/versions/${id}/active`, { isActive: Boolean(isActive) });
  },
  /**
   * Deep-clone a question version's steps + items into a NEW draft version.
   * @param {number} id source question version id
   * @param {{versionCode:string, description?:?string}} body
   * @returns {Promise<TaxQuestionVersionV2Dto>} the new (draft) version
   */
  cloneVersion(id, body) {
    return post(`${ROOT}/versions/${id}/clone`, body || {});
  },
  /** @returns {Promise<boolean>} (steps + items cascade-deleted server-side) */
  deleteVersion(id) {
    return del(`${ROOT}/versions/${id}`);
  },

  // ── Export / Import (move a question set between machines) ───────────
  /**
   * Export one question version (header + ALL steps + ALL items, inactive rows included) as a
   * portable bundle — the replacement for the hand-run python migrate scripts.
   *
   * The bundle carries no server ids and no audit columns: everything cross-references by string
   * code, so the same file imports onto Local / Dev / UAT alike. Returns the bundle OBJECT (the
   * endpoint answers with a normal `ResponseModel`, not a file download) — the caller turns it into
   * a `.json` download with a Blob.
   *
   * @param {number} questionVersionId source question version id
   * @returns {Promise<TaxQuestionBundleV2Dto>} 404 → rejected Error
   */
  exportVersion(questionVersionId) {
    return get(`${ROOT}/versions/${questionVersionId}/export`);
  },
  /**
   * Import a bundle as a NEW question version. The new version is always created inactive, so an
   * import can never knock the live set off the air.
   *
   * Anything the target machine is missing (a `fieldKey` with no rule, a formula with no matching
   * `functionCode`) is REPORTED in `warnings` and the import proceeds — it never 400s the whole
   * file. Read `warnings` / `dropped*Count` and surface them; a silent partial load is worse than
   * a refusal.
   *
   * @param {Object} args
   * @param {TaxQuestionBundleV2Dto} args.bundle The parsed file. Untrusted input — only its
   *   content is used; `bundle.source.*` is display-only and never resolves the target.
   * @param {number} args.taxConfigVersionId Config version the new set is created under. Comes from
   *   the UI (the row the Import button sits under), NEVER from the file.
   * @param {?string} [args.versionCode] Code for the new version. Empty ⇒ the file's
   *   `source.questionVersionCode`. Either way a collision auto-increments (`Q3` → `Q3-2`) and the
   *   result reports `versionCodeAdjusted: true`.
   * @param {?string} [args.description] Empty ⇒ the file's `source.description`.
   * @param {boolean} [args.dryRun=false] `true` ⇒ run the whole remap and report counts + warnings
   *   but write NOTHING to the database — this is what powers the preview panel. `questionVersionID`
   *   comes back `null` on a dry run.
   * @returns {Promise<ImportTaxQuestionBundleV2Result>}
   */
  importBundle({ bundle, taxConfigVersionId, versionCode, description, dryRun } = {}) {
    return post(`${ROOT}/import`, {
      bundle,
      taxConfigVersionID: taxConfigVersionId,
      versionCode: versionCode || null,
      description: description || null,
      dryRun: Boolean(dryRun),
    });
  },

  // ── Question Steps (dedicated CRUD) ──────────────────────────────────
  /** @returns {Promise<TaxQuestionStepV2Dto[]>} */
  getSteps(versionId, includeInactive = true) {
    return get(`${ROOT}/versions/${versionId}/steps?includeInactive=${includeInactive}`);
  },
  /** @returns {Promise<TaxQuestionStepV2Dto>} */
  getStep(stepId) {
    return get(`${ROOT}/steps/${stepId}`);
  },
  /** @returns {Promise<TaxQuestionStepV2Dto>} */
  createStep(versionId, body) {
    return post(`${ROOT}/versions/${versionId}/steps`, body);
  },
  /** @returns {Promise<TaxQuestionStepV2Dto>} */
  updateStep(stepId, body) {
    return put(`${ROOT}/steps/${stepId}`, body);
  },
  /** @returns {Promise<boolean>} */
  setStepActive(stepId, isActive) {
    return patch(`${ROOT}/steps/${stepId}/active`, { isActive: Boolean(isActive) });
  },
  /** @returns {Promise<boolean>} */
  deleteStep(stepId) {
    return del(`${ROOT}/steps/${stepId}`);
  },

  // ── Question Items ───────────────────────────────────────────────────
  /** @returns {Promise<TaxQuestionItemV2Dto[]>} */
  getItems(versionId, includeInactive = true) {
    return get(`${ROOT}/versions/${versionId}/items?includeInactive=${includeInactive}`);
  },
  /** @returns {Promise<TaxQuestionItemV2Dto>} */
  getItem(itemId) {
    return get(`${ROOT}/items/${itemId}`);
  },
  /** @returns {Promise<TaxQuestionItemV2Dto>} */
  createItem(versionId, body) {
    return post(`${ROOT}/versions/${versionId}/items`, body);
  },
  /** @returns {Promise<TaxQuestionItemV2Dto>} */
  updateItem(itemId, body) {
    return put(`${ROOT}/items/${itemId}`, body);
  },
  /** @returns {Promise<boolean>} */
  setItemActive(itemId, isActive) {
    return patch(`${ROOT}/items/${itemId}/active`, { isActive: Boolean(isActive) });
  },
  /** @returns {Promise<boolean>} */
  deleteItem(itemId) {
    return del(`${ROOT}/items/${itemId}`);
  },
  /**
   * Replace the whole step + item set of a version (builder "Save") — transactional
   * all-or-nothing. Accepts either a plain items array (legacy `{ items }` body) or
   * an object `{ steps, items }`. When `steps` is omitted the server leaves existing
   * step rows untouched (backward-compatible).
   * @param {number} versionId
   * @param {Object[]|{steps?:Object[],items:Object[],comment?:string}} payload
   * @returns {Promise<TaxQuestionItemV2Dto[]>} the new item set (+ orphan warnings in Message)
   */
  bulkSaveItems(versionId, payload) {
    let body;
    if (Array.isArray(payload)) {
      body = { items: payload };
    } else {
      const p = payload || {};
      body = { items: Array.isArray(p.items) ? p.items : [] };
      if (p.steps !== undefined) body.steps = Array.isArray(p.steps) ? p.steps : [];
      // บันทึกช่วยจำของรอบนี้ → ไปโผล่เป็น `comment` ของแถวประวัติ · ส่งเฉพาะเมื่อมีข้อความจริง
      // (สตริงว่างจะกลายเป็น comment ว่างในประวัติ ซึ่งอ่านต่างจาก "ไม่ได้เขียนอะไร")
      if (typeof p.comment === 'string' && p.comment.trim()) body.comment = p.comment.trim();
    }
    return put(`${ROOT}/versions/${versionId}/items/bulk`, body);
  },

  // ── Save history (snapshots of one question version) ─────────────────
  /**
   * List the save-history snapshots of one question version, newest first.
   * The rows come back WITHOUT `payloadJson` (it is null on every row) — fetch a
   * single snapshot with `getSnapshot` when the payload is actually needed.
   * A version that has never been saved yields `[]` (200, not 404).
   * @param {number} versionId question version id
   * @returns {Promise<TaxQuestionSnapshotV2Dto[]>} newest (`isCurrent: true`) first
   */
  getHistory(versionId) {
    return get(`${ROOT}/versions/${versionId}/history`);
  },
  /**
   * One snapshot WITH its `payloadJson` — the raw JSON string
   * `{"steps":[…],"items":[…]}` (parse it with `utils/taxQuestionSnapshotDiff.js`).
   * @param {number} snapshotId
   * @returns {Promise<TaxQuestionSnapshotV2Dto>} 404 → rejected Error
   */
  getSnapshot(snapshotId) {
    return get(`${ROOT}/history/${snapshotId}`);
  },
  /**
   * Restore a snapshot over the CURRENT config of its own question version — the
   * `questionVersionID` never changes and existing `QuestionItemID`s are kept, so
   * customer answers stay attached. The restore itself is recorded as a new
   * snapshot, so it can be rolled back again.
   * @param {number} snapshotId
   * @param {?{comment?:?string}} body optional note (defaults server-side to `Restored from v{N}`)
   * @returns {Promise<{questionVersionID:number, restoredFromNo:number, stepCount:number,
   *   itemCount:number, warnings:string[]}>}
   */
  restoreSnapshot(snapshotId, body) {
    return post(`${ROOT}/history/${snapshotId}/restore`, body || {});
  },

  // ── SHORT_FORM template helpers ──────────────────────────────────────
  /**
   * SHORT_FORM only — the built-in default JSON template, for the «ใส่โครงเริ่มต้น»
   * button in the item editor (seeds `item.shortFormTemplateJson`). Returns the
   * template STRING directly (already unwrapped from `{ templateJson }`), not the object.
   * @returns {Promise<string>} templateJson
   */
  async getShortFormDefaultTemplate() {
    const data = await get(`${ROOT}/shortform/default-template`);
    return data.templateJson;
  },

  // ── FILING_CHOICE template helpers ───────────────────────────────────
  /**
   * FILING_CHOICE only — the built-in default JSON template, for the «ใส่โครงเริ่มต้น»
   * button in the item editor (seeds `item.filingTemplateJson`). Returns the
   * template STRING directly (already unwrapped from `{ templateJson }`), not the object.
   *
   * The template only SHAPES the compare card; which tile wins the badge and which one carries the
   * "ต้องจ่ายเพิ่ม" row is still decided in code, so seeding this box changes the frame, never the verdict.
   * @returns {Promise<string>} templateJson
   */
  async getFilingDefaultTemplate() {
    const data = await get(`${ROOT}/filing/default-template`);
    return data.templateJson;
  },

  // ── SIMULATE template helpers ────────────────────────────────────────
  /**
   * SIMULATE only — the built-in default JSON template, for the «ใส่โครงเริ่มต้น»
   * button in the item editor (seeds `item.simulationTemplateJson`). Returns the
   * template STRING directly (already unwrapped from `{ templateJson }`), not the object.
   *
   * The template only SHAPES the what-if card; where a bar's floor sits, which rows may be compared
   * and whether a saving may be claimed are still decided in code, so seeding this box changes the
   * frame, never the figures.
   * @returns {Promise<string>} templateJson
   */
  async getSimulationDefaultTemplate() {
    const data = await get(`${ROOT}/simulation/default-template`);
    return data.templateJson;
  },

  // ── Card preview (item editor's live preview) ────────────────────────
  /**
   * Render the card an item's UNSAVED config/template would produce, against synthetic sample
   * answers. Stateless: nothing is read from or written to the database, which is what lets the
   * preview show what the author is typing rather than what was last saved.
   *
   * Build `payload` with `utils/taxCardPreview.js` → `buildPreviewRequest(item, opts)`.
   * @param {{itemKind:string, cardConfigJson?:?string, summaryTemplateJson?:?string,
   *   shortFormConfigJson?:?string, shortFormTemplateJson?:?string, filingTemplateJson?:?string,
   *   simulationTemplateJson?:?string, fieldKey?:?string, lang?:?string}} payload
   * @returns {Promise<{card:?Object, raw:?string, errors:string[], cardType:?string}>}
   *   `card` is null when the config/template could not be read — `errors` (Thai) says why.
   */
  previewCard(payload) {
    return post(`${ROOT}/card/preview`, payload || {});
  },

  // ── App Bar group state (SAVE_STATE `stateGroup` dropdown) ───────────
  /**
   * The App Bar "Pending in Specific State" group states, as dropdown rows. Source:
   * `GET api/v1/BarManagement/options` → `conditionStateOptions: [{ name, valueString }]`.
   * Cached per module: a successful load is reused; a failed one is forgotten so the next call retries.
   * @returns {Promise<{value:string, title:string}[]>}
   */
  getStateGroupOptions() {
    if (!stateGroupOptionsPromise) {
      stateGroupOptionsPromise = get(BAR_OPTIONS_URL)
        .then((data) => {
          const list = data && Array.isArray(data.conditionStateOptions) ? data.conditionStateOptions : [];
          return list
            .filter((o) => o && typeof o.valueString === 'string' && o.valueString)
            .map((o) => ({ value: o.valueString, title: o.name || o.valueString }));
        })
        .catch((e) => {
          stateGroupOptionsPromise = null;
          throw e;
        });
    }
    return stateGroupOptionsPromise;
  },
};

export default taxQuestionV2Api;
